import CrmObjectsLive from "./CrmObjectsLive";
import { StatusTextBadge as Badge } from "../../../components/base/StatusTextBadge";
import type { CrmObject } from "../services/crmObjectsService";
import type { CrudColumn, CrudRow } from "../types";

// ===========================================================================
// The CRM sections backed by the entities the HubSpot suite integration added.
// Each one is <CrmObjectsLive> with its column set — the add/edit form is
// generated from the server catalog, so only presentation lives here.
//
// These replace the demo scaffolds the same menu entries used to render.
// ===========================================================================

type Row = CrmObject & CrudRow;

/** Firestore timestamps arrive serialized; render them as a short local date. */
function fmt(value: unknown): string {
  if (!value) return "—";
  if (typeof value === "object") {
    const v = value as { _seconds?: number; seconds?: number };
    const secs = v._seconds ?? v.seconds;
    if (typeof secs === "number") return new Date(secs * 1000).toLocaleDateString("es-MX");
    return "—";
  }
  const d = new Date(String(value));
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("es-MX");
}

const text = (value: unknown) => (value == null || value === "" ? "—" : String(value));

// --- Tareas ----------------------------------------------------------------

const taskColumns: CrudColumn<Row>[] = [
  { key: "title", label: "Tarea", render: (r) => text(r.title) },
  { key: "type", label: "Tipo", render: (r) => text(r.type) },
  { key: "dueAt", label: "Vence", render: (r) => fmt(r.dueAt) },
  { key: "priority", label: "Prioridad", align: "center", render: (r) => <Badge value={r.priority} /> },
  { key: "status", label: "Estado", align: "center", render: (r) => <Badge value={r.status} /> },
];

export function TareasLive() {
  return (
    <CrmObjectsLive
      type="tasks"
      title="Tareas"
      subtitle="Pendientes del equipo, sincronizadas con HubSpot"
      icon="ri-task-line"
      columns={taskColumns}
    />
  );
}

// --- Notas -----------------------------------------------------------------

const noteColumns: CrudColumn<Row>[] = [
  { key: "body", label: "Nota", render: (r) => text(r.body) },
  { key: "noteAt", label: "Fecha", render: (r) => fmt(r.noteAt) },
  { key: "contactId", label: "Contacto", render: (r) => text(r.contactId) },
  { key: "dealId", label: "Negocio", render: (r) => text(r.dealId) },
];

export function NotasLive() {
  return (
    <CrmObjectsLive
      type="notes"
      title="Notas"
      subtitle="Notas de los comerciales sobre contactos, empresas y negocios"
      icon="ri-sticky-note-line"
      columns={noteColumns}
    />
  );
}

// --- Tickets ---------------------------------------------------------------

const ticketColumns: CrudColumn<Row>[] = [
  { key: "subject", label: "Asunto", render: (r) => text(r.subject) },
  { key: "category", label: "Categoría", render: (r) => text(r.category) },
  { key: "channel", label: "Canal", render: (r) => text(r.channel) },
  { key: "priority", label: "Prioridad", align: "center", render: (r) => <Badge value={r.priority} /> },
  { key: "status", label: "Estado", align: "center", render: (r) => <Badge value={r.status} /> },
];

export function TicketsLive() {
  return (
    <CrmObjectsLive
      type="tickets"
      title="Tickets"
      subtitle="Casos de servicio sincronizados con el Service Hub de HubSpot"
      icon="ri-customer-service-2-line"
      columns={ticketColumns}
    />
  );
}

// --- Correos registrados ---------------------------------------------------

const emailColumns: CrudColumn<Row>[] = [
  { key: "subject", label: "Asunto", render: (r) => text(r.subject) },
  { key: "to", label: "Para", render: (r) => text(r.to) },
  { key: "from", label: "De", render: (r) => text(r.from) },
  { key: "sentAt", label: "Enviado", render: (r) => fmt(r.sentAt) },
  { key: "direction", label: "Dirección", align: "center", render: (r) => <Badge value={r.direction} /> },
];

export function CorreosLive() {
  return (
    <CrmObjectsLive
      type="emails"
      title="Correos registrados"
      subtitle="Correos uno a uno registrados en la ficha del contacto"
      icon="ri-mail-line"
      columns={emailColumns}
    />
  );
}

// --- Productos -------------------------------------------------------------

// Productos tiene su propia pantalla: el catálogo junta los productos del CRM,
// las licencias de la plataforma y lo importado de HubSpot. Ver ProductosSection.
export { default as ProductosLive } from "./ProductosSection";

// --- Cotizaciones ----------------------------------------------------------

// Cotizaciones tiene su propia pantalla de seguimiento (KPIs, temperatura,
// vigencia y documento): ver QuotesSection.
export { default as CotizacionesLive } from "./QuotesSection";

// --- Marketing Hub mirrors (read-only) -------------------------------------

const campaignColumns: CrudColumn<Row>[] = [
  { key: "name", label: "Campaña", render: (r) => text(r.name) },
  { key: "subject", label: "Asunto", render: (r) => text(r.subject) },
  { key: "publishedAt", label: "Publicada", render: (r) => fmt(r.publishedAt) },
  {
    key: "stats",
    label: "Envíos / Aperturas / Clics",
    align: "center",
    render: (r) => {
      const s = r.stats as { sent?: number; open?: number; click?: number } | null;
      return s ? `${s.sent ?? 0} / ${s.open ?? 0} / ${s.click ?? 0}` : "—";
    },
  },
  { key: "state", label: "Estado", align: "center", render: (r) => <Badge value={r.state} /> },
];

export function CampanasLive() {
  return (
    <CrmObjectsLive
      type="campaigns"
      title="Campañas"
      subtitle="Correos de marketing enviados desde HubSpot, con sus métricas"
      icon="ri-rocket-line"
      columns={campaignColumns}
    />
  );
}

const formColumns: CrudColumn<Row>[] = [
  { key: "name", label: "Formulario", render: (r) => text(r.name) },
  { key: "formType", label: "Tipo", render: (r) => text(r.formType) },
  {
    key: "fields",
    label: "Campos",
    align: "center",
    render: (r) => (Array.isArray(r.fields) ? String(r.fields.length) : "—"),
  },
  { key: "archived", label: "Estado", align: "center", render: (r) => <Badge value={r.archived ? "archivado" : "activo"} /> },
];

export function FormulariosLive() {
  return (
    <CrmObjectsLive
      type="forms"
      title="Formularios"
      subtitle="Formularios de HubSpot; cada envío entra a la bandeja como lead"
      icon="ri-file-list-3-line"
      columns={formColumns}
    />
  );
}

const submissionColumns: CrudColumn<Row>[] = [
  { key: "formName", label: "Formulario", render: (r) => text(r.formName) },
  { key: "email", label: "Correo", render: (r) => text(r.email) },
  { key: "submittedAt", label: "Enviado", render: (r) => fmt(r.submittedAt) },
  { key: "pageUrl", label: "Página", render: (r) => text(r.pageUrl) },
];

export function EnviosFormularioLive() {
  return (
    <CrmObjectsLive
      type="form_submissions"
      title="Envíos de formularios"
      subtitle="Cada envío se convierte además en un lead de la bandeja"
      icon="ri-inbox-archive-line"
      columns={submissionColumns}
    />
  );
}

const segmentColumns: CrudColumn<Row>[] = [
  { key: "name", label: "Segmento", render: (r) => text(r.name) },
  { key: "listType", label: "Tipo", render: (r) => text(r.listType) },
  { key: "size", label: "Miembros", align: "center", render: (r) => text(r.size) },
];

export function SegmentosLive() {
  return (
    <CrmObjectsLive
      type="segments"
      title="Segmentos"
      subtitle="Listas de HubSpot reflejadas como segmentos del CRM"
      icon="ri-group-2-line"
      columns={segmentColumns}
    />
  );
}
